import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Button,
  Avatar,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Badge,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment
} from '@mui/material';
import {
  People,
  PersonAdd,
  Check,
  Close,
  Block,
  Refresh,
  AdminPanelSettings,
  Email,
  Schedule,
  VerifiedUser,
  Warning,
  Business,
  Add,
  Edit,
  Delete,
  Group,
  SupervisorAccount,
  Search,
  FilterList,
  MoreVert
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getAuthHeaders } from '../config/api';

// Helper functie om runtime configuratie te gebruiken
const getCentralBackendURL = () => {
  // Runtime configuratie (Docker container)
  if (window.ENV_CONFIG && window.ENV_CONFIG.REACT_APP_CENTRAL_BACKEND_URL) {
    return window.ENV_CONFIG.REACT_APP_CENTRAL_BACKEND_URL;
  }
  // Build-time configuratie (fallback)
  return process.env.REACT_APP_CENTRAL_BACKEND_URL || 'http://localhost:8000';
};

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  overflow: 'hidden'
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.05),
  '& .MuiTableCell-head': {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
  }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  '&:nth-of-type(even)': {
    backgroundColor: alpha(theme.palette.grey[50], 0.5),
  }
}));

const StatCard = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  color: 'white',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  textAlign: 'center',
  '& .MuiTypography-root': {
    color: 'white'
  }
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  marginLeft: theme.spacing(0.5),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  }
}));

const UserManagementPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  
  // State management
  const [tab, setTab] = useState(0); // Start met gebruikers tab
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, user: null });
  const [confirmDeleteCompanyDialog, setConfirmDeleteCompanyDialog] = useState({ open: false, company: null });
  const [createUserDialog, setCreateUserDialog] = useState(false);
  const [createCompanyDialog, setCreateCompanyDialog] = useState(false);
  const [editCompanyDialog, setEditCompanyDialog] = useState({ open: false, company: null });
  const [addUserToCompanyDialog, setAddUserToCompanyDialog] = useState({ open: false, companyId: null });
  
  // Form states
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    password: '',
    system_role: 'company_user',
    company_id: '',
    company_role: 'company_user',
    is_active: true
  });
  
  const [newCompany, setNewCompany] = useState({
    name: '',
    description: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: ''
  });
  
  const [editCompanyData, setEditCompanyData] = useState(null);
  
  const [addUserToCompany, setAddUserToCompany] = useState({
    user_email: '',
    role: 'company_user'
  });

  // Table state
  const [pendingUsersPage, setPendingUsersPage] = useState(0);
  const [pendingUsersRowsPerPage, setPendingUsersRowsPerPage] = useState(10);
  const [allUsersPage, setAllUsersPage] = useState(0);
  const [allUsersRowsPerPage, setAllUsersRowsPerPage] = useState(10);
  const [companiesPage, setCompaniesPage] = useState(0);
  const [companiesRowsPerPage, setCompaniesRowsPerPage] = useState(10);
  
  // Search filters
  const [userSearchFilter, setUserSearchFilter] = useState('');
  const [companySearchFilter, setCompanySearchFilter] = useState('');

  // Helper function to format error messages
  const formatErrorMessage = (errorData) => {
    if (typeof errorData === 'string') {
      return errorData;
    }
    
    if (errorData && typeof errorData === 'object') {
      // Handle validation errors (array of error objects)
      if (Array.isArray(errorData)) {
        return errorData.map(err => {
          if (typeof err === 'string') return err;
          if (err.msg) return err.msg;
          if (err.detail) return err.detail;
          return JSON.stringify(err);
        }).join(', ');
      }
      
      // Handle single error object
      if (errorData.detail) {
        return errorData.detail;
      }
      
      if (errorData.message) {
        return errorData.message;
      }
      
      if (errorData.msg) {
        return errorData.msg;
      }
      
      // Last resort: stringify the object
      return JSON.stringify(errorData);
    }
    
    return 'Er is een onbekende fout opgetreden';
  };

  // Check of gebruiker admin is (Super admin of Company admin)
  const isAdmin = user?.id === 1 || user?.system_role === 'super_admin' || 
    (user?.company_roles && user.company_roles.some(role => role.role === 'company_admin'));
  
  // Check of gebruiker super admin is (alleen super admins kunnen bedrijven beheren)
  const isSuperAdmin = user?.id === 1 || user?.system_role === 'super_admin';

  useEffect(() => {
    if (!isAdmin) {
      setError('Geen toegang tot gebruikersbeheer');
      return;
    }
    loadData();
  }, [isAdmin]);

  // Helper functies voor tab management
  const getCompaniesTabIndex = () => isSuperAdmin ? 1 : -1;
  const getApprovalsTabIndex = () => {
    if (!pendingUsers.length) return -1;
    return isSuperAdmin ? 2 : 1;
  };

  // Reset tab als goedkeuringen tab verdwijnt of niet meer beschikbaar is
  useEffect(() => {
    const approvalsTabIndex = getApprovalsTabIndex();
    const companiesTabIndex = getCompaniesTabIndex();
    
    // Als we op een tab zitten die niet meer bestaat
    if ((tab === approvalsTabIndex && pendingUsers.length === 0) ||
        (tab === companiesTabIndex && !isSuperAdmin)) {
      setTab(0); // Ga terug naar gebruikers tab
    }
  }, [pendingUsers.length, tab, isSuperAdmin]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadPendingUsers(),
        loadAllUsers(),
        loadCompanies(),
        loadStats()
      ]);
    } catch (err) {
      setError('Kon gegevens niet laden');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingUsers = async () => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${getCentralBackendURL()}/api/v1/admin/users/pending`, {
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPendingUsers(data);
      } else {
        throw new Error('Failed to load pending users');
      }
    } catch (err) {
      console.error('Error loading pending users:', err);
    }
  };

  const loadAllUsers = async () => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${getCentralBackendURL()}/api/v1/admin/users`, {
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAllUsers(data);
      } else {
        throw new Error('Failed to load all users');
      }
    } catch (err) {
      console.error('Error loading all users:', err);
    }
  };

  const loadCompanies = async () => {
    // Alleen bedrijven laden voor super admins
    if (!isSuperAdmin) {
      setCompanies([]);
      return;
    }
    
    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${getCentralBackendURL()}/api/v1/admin/companies`, {
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCompanies(data);
      } else {
        throw new Error('Failed to load companies');
      }
    } catch (err) {
      console.error('Error loading companies:', err);
    }
  };

  const loadStats = async () => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${getCentralBackendURL()}/api/v1/admin/stats`, {
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        throw new Error('Failed to load stats');
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handleUserAction = async (userId, action) => {
    setActionLoading(`${action}_${userId}`);
    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${getCentralBackendURL()}/api/v1/admin/users/${userId}/${action}`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setSuccess(result.message);
        await loadData(); // Herlaad alle gegevens
      } else {
        const errorData = await response.json();
        setError(formatErrorMessage(errorData));
      }
    } catch (err) {
      setError('Er ging iets mis bij het uitvoeren van ' + action);
    } finally {
      setActionLoading(null);
      setConfirmDialog({ open: false, action: null, user: null });
    }
  };

  const handleCreateUser = async () => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${getCentralBackendURL()}/api/v1/admin/users`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newUser)
      });

      if (response.ok) {
        setSuccess('Gebruiker succesvol aangemaakt');
        setCreateUserDialog(false);
        // Reset form voor volgende gebruik
        const currentUserCompany = getCurrentUserCompany();
        if (isSuperAdmin) {
          setNewUser({
            email: '',
            name: '',
            password: '',
            system_role: 'company_user',
            company_id: '',
            company_role: 'company_user',
            is_active: true
          });
        } else if (currentUserCompany) {
          setNewUser({
            email: '',
            name: '',
            password: '',
            system_role: 'company_user',
            company_id: currentUserCompany.company_id || '',
            company_role: 'company_user',
            is_active: true
          });
        }
        await loadData();
      } else {
        const errorData = await response.json();
        setError(formatErrorMessage(errorData));
      }
    } catch (err) {
      setError('Er ging iets mis bij het aanmaken van de gebruiker');
    }
  };

  const handleCreateCompany = async () => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${getCentralBackendURL()}/api/v1/admin/companies`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCompany)
      });

      if (response.ok) {
        setSuccess('Bedrijf succesvol aangemaakt');
        setCreateCompanyDialog(false);
        setNewCompany({
          name: '',
          description: '',
          contact_email: '',
          contact_phone: '',
          address: '',
          city: '',
          postal_code: '',
          country: ''
        });
        await loadData();
      } else {
        const errorData = await response.json();
        setError(formatErrorMessage(errorData));
      }
    } catch (err) {
      setError('Er ging iets mis bij het aanmaken van het bedrijf');
    }
  };

  const handleAddUserToCompanySubmit = async () => {
    try {
      const authHeaders = getAuthHeaders();
              const response = await fetch(`${getCentralBackendURL()}/api/v1/admin/companies/${addUserToCompanyDialog.companyId}/users`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(addUserToCompany)
      });

      if (response.ok) {
        setSuccess('Gebruiker succesvol toegevoegd aan bedrijf');
        setAddUserToCompanyDialog({ open: false, companyId: null });
        setAddUserToCompany({ user_email: '', role: 'company_user' });
        await loadData();
      } else {
        const errorData = await response.json();
        setError(formatErrorMessage(errorData));
      }
    } catch (err) {
      setError('Er ging iets mis bij het toevoegen van de gebruiker');
    }
  };

  const handleUpdateCompany = async () => {
    if (!editCompanyDialog.company) return;

    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${getCentralBackendURL()}/api/v1/admin/companies/${editCompanyDialog.company.id}`, {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editCompanyData)
      });

      if (response.ok) {
        setSuccess('Bedrijf succesvol bijgewerkt');
        setEditCompanyDialog({ open: false, company: null });
        await loadData();
      } else {
        const errorData = await response.json();
        setError(formatErrorMessage(errorData));
      }
    } catch (err) {
      setError('Er ging iets mis bij het bijwerken van het bedrijf');
    }
  };

  const handleDeleteCompany = async (companyId) => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${getCentralBackendURL()}/api/v1/admin/companies/${companyId}`, {
        method: 'DELETE',
        headers: {
          ...authHeaders
        }
      });

      if (response.ok) {
        setSuccess('Bedrijf succesvol verwijderd');
        await loadData();
      } else {
        const errorData = await response.json();
        setError(formatErrorMessage(errorData));
      }
    } catch (err) {
      setError('Er ging iets mis bij het verwijderen van het bedrijf');
    } finally {
      setConfirmDeleteCompanyDialog({ open: false, company: null });
    }
  };

  const openConfirmDialog = (action, user) => {
    setConfirmDialog({ open: true, action, user });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('nl-NL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUserStatusColor = (user) => {
    if (!user.is_active) return 'warning';
    return 'success';
  };

  const getUserStatusText = (user) => {
    if (!user.is_active) return 'Wacht op goedkeuring';
    return 'Actief';
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      super_admin: 'Super Admin',
      company_admin: 'Bedrijf Beheerder',
      company_user: 'Gebruiker'
    };
    return roleNames[role] || role;
  };

  const getRoleColor = (role) => {
    const roleColors = {
      super_admin: 'error',
      company_admin: 'warning',
      company_user: 'default'
    };
    return roleColors[role] || 'default';
  };

  const handleOpenEditCompany = (company) => {
        setEditCompanyData({ ...company });
    setEditCompanyDialog({ open: true, company: company });
  };

  // Helper functie om het huidige bedrijf van een company admin te krijgen
  const getCurrentUserCompany = () => {
    if (!user?.company_roles || user.company_roles.length === 0) return null;
    // Neem het eerste bedrijf waar de gebruiker admin is
    const adminRole = user.company_roles.find(role => role.role === 'company_admin');
    return adminRole || user.company_roles[0];
  };

  // Reset newUser vorm wanneer dialog opent
  const handleOpenCreateUserDialog = () => {
    const currentUserCompany = getCurrentUserCompany();
    
    if (isSuperAdmin) {
      // Super admin - lege form
      setNewUser({
        email: '',
        name: '',
        password: '',
        system_role: 'company_user',
        company_id: '',
        company_role: 'company_user',
        is_active: true
      });
    } else if (currentUserCompany) {
      // Company admin - vooraf ingevuld met eigen bedrijf
      setNewUser({
        email: '',
        name: '',
        password: '',
        system_role: 'company_user',
        company_id: currentUserCompany.company_id || '',
        company_role: 'company_user',
        is_active: true
      });
    }
    
    setCreateUserDialog(true);
  };

  if (!isAdmin) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          U heeft geen toegang tot deze pagina. Alleen beheerders kunnen gebruikers beheren.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress size={48} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Gegevens laden...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AdminPanelSettings sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Systeem Beheer
          </Typography>
          <Box sx={{ ml: 'auto' }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadData}
              disabled={loading}
            >
              Vernieuwen
            </Button>
          </Box>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Beheer bedrijven, gebruikers en rollen
        </Typography>
      </Box>

      {/* Statistieken */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Business sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.total_companies}
                  </Typography>
                  <Typography variant="body2">
                    Bedrijven
                  </Typography>
                </Box>
              </Box>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <People sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.total_users}
                  </Typography>
                  <Typography variant="body2">
                    Totaal gebruikers
                  </Typography>
                </Box>
              </Box>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard sx={{ background: 'linear-gradient(135deg, #4CAF50, #2E7D32)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <VerifiedUser sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.active_users}
                  </Typography>
                  <Typography variant="body2">
                    Actieve gebruikers
                  </Typography>
                </Box>
              </Box>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard sx={{ background: 'linear-gradient(135deg, #FF9800, #F57C00)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Schedule sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.pending_users}
                  </Typography>
                  <Typography variant="body2">
                    Wacht op goedkeuring
                  </Typography>
                </Box>
              </Box>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard sx={{ background: 'linear-gradient(135deg, #9C27B0, #6A1B9A)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonAdd sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.total_environments}
                  </Typography>
                  <Typography variant="body2">
                    Environments
                  </Typography>
                </Box>
              </Box>
            </StatCard>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(e, newValue) => setTab(newValue)} variant="fullWidth">
          <Tab label="Gebruikers" icon={<People />} />
          {isSuperAdmin && (
            <Tab label="Bedrijven" icon={<Business />} />
          )}
          {pendingUsers.length > 0 && (
            <Tab 
              label={
                <Badge badgeContent={pendingUsers.length} color="warning" max={99}>
                  Goedkeuringen
                </Badge>
              } 
              icon={<Schedule />} 
            />
          )}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {/* Gebruikers Tab - Altijd tab 0 */}
      {tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Alle gebruikers ({allUsers.filter(user => 
                user.name.toLowerCase().includes(userSearchFilter.toLowerCase()) ||
                user.email.toLowerCase().includes(userSearchFilter.toLowerCase())
              ).length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Zoek gebruikers..."
                value={userSearchFilter}
                onChange={(e) => setUserSearchFilter(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 250 }}
              />
              <Button
                variant="contained"
                startIcon={<PersonAdd />}
                onClick={handleOpenCreateUserDialog}
              >
                Gebruiker Toevoegen
              </Button>
            </Box>
          </Box>
          
          <StyledTableContainer component={Paper}>
            <Table>
              <StyledTableHead>
                <TableRow>
                  <TableCell>Gebruiker</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Systeem Rol</TableCell>
                  <TableCell>Bedrijven</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Laatste Login</TableCell>
                  <TableCell align="center">Acties</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {allUsers
                  .filter(user => 
                    user.name.toLowerCase().includes(userSearchFilter.toLowerCase()) ||
                    user.email.toLowerCase().includes(userSearchFilter.toLowerCase())
                  )
                  .slice(allUsersPage * allUsersRowsPerPage, allUsersPage * allUsersRowsPerPage + allUsersRowsPerPage)
                  .map((userItem) => (
                  <StyledTableRow key={userItem.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          src={userItem.picture_url} 
                          sx={{ 
                            mr: 2, 
                            bgcolor: getUserStatusColor(userItem) + '.main',
                            width: 40, 
                            height: 40 
                          }}
                        >
                          {userItem.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {userItem.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {userItem.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {userItem.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getRoleDisplayName(userItem.system_role)}
                        color={getRoleColor(userItem.system_role)}
                        size="small"
                        icon={<AdminPanelSettings />}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {userItem.company_roles && userItem.company_roles.length > 0 ? (
                          userItem.company_roles.slice(0, 2).map((companyRole, index) => (
                            <Tooltip 
                              key={index}
                              title={`${companyRole.company_name} - ${getRoleDisplayName(companyRole.role)}`}
                            >
                              <Chip
                                label={companyRole.company_name}
                                size="small"
                                variant="outlined"
                                color="primary"
                              />
                            </Tooltip>
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Geen bedrijf
                          </Typography>
                        )}
                        {userItem.company_roles && userItem.company_roles.length > 2 && (
                          <Chip
                            label={`+${userItem.company_roles.length - 2}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getUserStatusText(userItem)}
                        color={getUserStatusColor(userItem)}
                        size="small"
                        icon={userItem.is_active ? <VerifiedUser /> : <Warning />}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {userItem.last_login ? formatDate(userItem.last_login) : 'Nooit'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Meer opties">
                        <ActionButton>
                          <MoreVert />
                        </ActionButton>
                      </Tooltip>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={allUsers.filter(user => 
                user.name.toLowerCase().includes(userSearchFilter.toLowerCase()) ||
                user.email.toLowerCase().includes(userSearchFilter.toLowerCase())
              ).length}
              page={allUsersPage}
              onPageChange={(event, newPage) => setAllUsersPage(newPage)}
              rowsPerPage={allUsersRowsPerPage}
              onRowsPerPageChange={(event) => {
                setAllUsersRowsPerPage(parseInt(event.target.value, 10));
                setAllUsersPage(0);
              }}
              labelRowsPerPage="Rijen per pagina:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} van ${count}`}
            />
          </StyledTableContainer>
        </Box>
      )}

      {/* Bedrijven Tab - Alleen voor super admins */}
      {isSuperAdmin && tab === getCompaniesTabIndex() && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Bedrijven ({companies.filter(company => 
                company.name.toLowerCase().includes(companySearchFilter.toLowerCase()) ||
                (company.contact_email && company.contact_email.toLowerCase().includes(companySearchFilter.toLowerCase()))
              ).length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Zoek bedrijven..."
                value={companySearchFilter}
                onChange={(e) => setCompanySearchFilter(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 250 }}
              />
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setCreateCompanyDialog(true)}
              >
                Bedrijf Toevoegen
              </Button>
            </Box>
          </Box>
          
          <StyledTableContainer component={Paper}>
            <Table>
              <StyledTableHead>
                <TableRow>
                  <TableCell>Bedrijf</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Gebruikers</TableCell>
                  <TableCell>Environments</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Aangemaakt</TableCell>
                  <TableCell align="center">Acties</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {companies
                  .filter(company => 
                    company.name.toLowerCase().includes(companySearchFilter.toLowerCase()) ||
                    (company.contact_email && company.contact_email.toLowerCase().includes(companySearchFilter.toLowerCase()))
                  )
                  .slice(companiesPage * companiesRowsPerPage, companiesPage * companiesRowsPerPage + companiesRowsPerPage)
                  .map((company) => (
                  <StyledTableRow key={company.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, bgcolor: 'primary.main', width: 40, height: 40 }}>
                          <Business />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {company.name}
                          </Typography>
                          {company.description && (
                            <Typography variant="caption" color="text.secondary">
                              {company.description.length > 50 
                                ? `${company.description.substring(0, 50)}...` 
                                : company.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {company.contact_email ? (
                        <Box>
                          <Typography variant="body2">
                            {company.contact_email}
                          </Typography>
                          {company.contact_phone && (
                            <Typography variant="caption" color="text.secondary">
                              {company.contact_phone}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Geen contact
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <People fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {company.user_count || 0}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {company.environment_count || 0}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={company.status || 'active'}
                        color={company.status === 'active' ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(company.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <Tooltip title="Gebruiker toevoegen">
                          <ActionButton
                            color="primary"
                            onClick={() => setAddUserToCompanyDialog({ open: true, companyId: company.id })}
                          >
                            <Group />
                          </ActionButton>
                        </Tooltip>
                        <Tooltip title="Bewerken">
                          <ActionButton
                            color="primary"
                            onClick={() => handleOpenEditCompany(company)}
                          >
                            <Edit />
                          </ActionButton>
                        </Tooltip>
                        <Tooltip title="Verwijderen">
                          <ActionButton
                            color="error"
                            onClick={() => setConfirmDeleteCompanyDialog({ open: true, company: company })}
                          >
                            <Delete />
                          </ActionButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={companies.filter(company => 
                company.name.toLowerCase().includes(companySearchFilter.toLowerCase()) ||
                (company.contact_email && company.contact_email.toLowerCase().includes(companySearchFilter.toLowerCase()))
              ).length}
              page={companiesPage}
              onPageChange={(event, newPage) => setCompaniesPage(newPage)}
              rowsPerPage={companiesRowsPerPage}
              onRowsPerPageChange={(event) => {
                setCompaniesRowsPerPage(parseInt(event.target.value, 10));
                setCompaniesPage(0);
              }}
              labelRowsPerPage="Rijen per pagina:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} van ${count}`}
            />
          </StyledTableContainer>
        </Box>
      )}

      {/* Goedkeuringen Tab - alleen als er pending users zijn */}
      {pendingUsers.length > 0 && tab === getApprovalsTabIndex() && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Wacht op goedkeuring ({pendingUsers.length})
            </Typography>
          </Box>
          
          <StyledTableContainer component={Paper}>
            <Table>
              <StyledTableHead>
                <TableRow>
                  <TableCell>Gebruiker</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Aangevraagd op</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Acties</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {pendingUsers
                  .slice(pendingUsersPage * pendingUsersRowsPerPage, pendingUsersPage * pendingUsersRowsPerPage + pendingUsersRowsPerPage)
                  .map((pendingUser) => (
                  <StyledTableRow key={pendingUser.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, bgcolor: 'warning.main', width: 40, height: 40 }}>
                          {pendingUser.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {pendingUser.name}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {pendingUser.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(pendingUser.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label="Wacht op goedkeuring"
                        color="warning"
                        size="small"
                        icon={<Schedule />}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <Tooltip title="Goedkeuren">
                          <ActionButton
                            color="success"
                            disabled={actionLoading === `approve_${pendingUser.id}`}
                            onClick={() => openConfirmDialog('approve', pendingUser)}
                          >
                            {actionLoading === `approve_${pendingUser.id}` ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Check />
                            )}
                          </ActionButton>
                        </Tooltip>
                        <Tooltip title="Afwijzen">
                          <ActionButton
                            color="error"
                            disabled={actionLoading === `reject_${pendingUser.id}`}
                            onClick={() => openConfirmDialog('reject', pendingUser)}
                          >
                            {actionLoading === `reject_${pendingUser.id}` ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Close />
                            )}
                          </ActionButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={pendingUsers.length}
              page={pendingUsersPage}
              onPageChange={(event, newPage) => setPendingUsersPage(newPage)}
              rowsPerPage={pendingUsersRowsPerPage}
              onRowsPerPageChange={(event) => {
                setPendingUsersRowsPerPage(parseInt(event.target.value, 10));
                setPendingUsersPage(0);
              }}
              labelRowsPerPage="Rijen per pagina:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} van ${count}`}
            />
          </StyledTableContainer>
        </Box>
      )}

      {/* Create User Dialog */}
      <Dialog open={createUserDialog} onClose={() => setCreateUserDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {isSuperAdmin 
            ? 'Nieuwe Gebruiker Toevoegen' 
            : `Nieuwe Gebruiker Toevoegen aan ${getCurrentUserCompany()?.company_name || 'Uw Bedrijf'}`
          }
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Naam"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Wachtwoord (optioneel)"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                helperText="Leeglaten voor automatisch wachtwoord"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Systeem Rol</InputLabel>
                <Select
                  value={newUser.system_role}
                  onChange={(e) => setNewUser({ ...newUser, system_role: e.target.value })}
                >
                  <MenuItem value="company_user">Gebruiker</MenuItem>
                  <MenuItem value="company_admin">Bedrijf Beheerder</MenuItem>
                  {isSuperAdmin && (
                    <MenuItem value="super_admin">Super Admin</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              {isSuperAdmin ? (
                <FormControl fullWidth>
                  <InputLabel>Bedrijf (optioneel)</InputLabel>
                  <Select
                    value={newUser.company_id}
                    onChange={(e) => setNewUser({ ...newUser, company_id: e.target.value })}
                  >
                    <MenuItem value="">Geen bedrijf</MenuItem>
                    {companies.map((company) => (
                      <MenuItem key={company.id} value={company.id}>
                        {company.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  label="Bedrijf"
                  value={(() => {
                    const currentCompany = getCurrentUserCompany();
                    if (currentCompany) {
                      return currentCompany.company_name || 'Uw bedrijf';
                    }
                    return 'Geen bedrijf toegewezen';
                  })()}
                  disabled
                  helperText="Gebruikers worden automatisch toegevoegd aan uw bedrijf"
                />
              )}
            </Grid>
            {(isSuperAdmin ? newUser.company_id : true) && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Bedrijf Rol</InputLabel>
                  <Select
                    value={newUser.company_role}
                    onChange={(e) => setNewUser({ ...newUser, company_role: e.target.value })}
                  >
                    <MenuItem value="company_user">Gebruiker</MenuItem>
                    <MenuItem value="company_admin">Bedrijf Beheerder</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateUserDialog(false)}>Annuleren</Button>
          <Button onClick={handleCreateUser} variant="contained">Aanmaken</Button>
        </DialogActions>
      </Dialog>

      {/* Create Company Dialog */}
      <Dialog open={createCompanyDialog} onClose={() => setCreateCompanyDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Nieuw Bedrijf Toevoegen</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bedrijfsnaam"
                value={newCompany.name}
                onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                value={newCompany.contact_email}
                onChange={(e) => setNewCompany({ ...newCompany, contact_email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Beschrijving"
                multiline
                rows={3}
                value={newCompany.description}
                onChange={(e) => setNewCompany({ ...newCompany, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Telefoon"
                value={newCompany.contact_phone}
                onChange={(e) => setNewCompany({ ...newCompany, contact_phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Land"
                value={newCompany.country}
                onChange={(e) => setNewCompany({ ...newCompany, country: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateCompanyDialog(false)}>Annuleren</Button>
          <Button onClick={handleCreateCompany} variant="contained">Aanmaken</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog 
        open={editCompanyDialog.open} 
        onClose={() => setEditCompanyDialog({ open: false, company: null })} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>Bedrijf Bewerken</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bedrijfsnaam"
                value={editCompanyData?.name || ''}
                onChange={(e) => setEditCompanyData({ ...editCompanyData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                value={editCompanyData?.contact_email || ''}
                onChange={(e) => setEditCompanyData({ ...editCompanyData, contact_email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Beschrijving"
                multiline
                rows={3}
                value={editCompanyData?.description || ''}
                onChange={(e) => setEditCompanyData({ ...editCompanyData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Telefoon"
                value={editCompanyData?.contact_phone || ''}
                onChange={(e) => setEditCompanyData({ ...editCompanyData, contact_phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Land"
                value={editCompanyData?.country || ''}
                onChange={(e) => setEditCompanyData({ ...editCompanyData, country: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editCompanyData?.status || 'active'}
                  onChange={(e) => setEditCompanyData({ ...editCompanyData, status: e.target.value })}
                >
                  <MenuItem value="active">Actief</MenuItem>
                  <MenuItem value="inactive">Inactief</MenuItem>
                  <MenuItem value="suspended">Opgeschort</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCompanyDialog({ open: false, company: null })}>Annuleren</Button>
          <Button 
            onClick={handleUpdateCompany} 
            variant="contained"
          >
            Opslaan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add User to Company Dialog */}
      <Dialog 
        open={addUserToCompanyDialog.open} 
        onClose={() => setAddUserToCompanyDialog({ open: false, companyId: null })} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Gebruiker Toevoegen aan Bedrijf</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email van gebruiker"
                type="email"
                value={addUserToCompany.user_email}
                onChange={(e) => setAddUserToCompany({ ...addUserToCompany, user_email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Rol binnen bedrijf</InputLabel>
                <Select
                  value={addUserToCompany.role}
                  onChange={(e) => setAddUserToCompany({ ...addUserToCompany, role: e.target.value })}
                >
                  <MenuItem value="company_user">Gebruiker</MenuItem>
                  <MenuItem value="company_admin">Bedrijf Beheerder</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddUserToCompanyDialog({ open: false, companyId: null })}>
            Annuleren
          </Button>
          <Button onClick={handleAddUserToCompanySubmit} variant="contained">
            Toevoegen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog (Users) */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: null, user: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmDialog.action === 'approve' && 'Gebruiker goedkeuren'}
          {confirmDialog.action === 'reject' && 'Gebruiker afwijzen'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.action === 'approve' && 
              `Weet u zeker dat u ${confirmDialog.user?.name} wilt goedkeuren? De gebruiker krijgt toegang tot het systeem.`}
            {confirmDialog.action === 'reject' && 
              `Weet u zeker dat u ${confirmDialog.user?.name} wilt afwijzen? Het account wordt permanent verwijderd.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialog({ open: false, action: null, user: null })}
          >
            Annuleren
          </Button>
          <Button
            onClick={() => handleUserAction(confirmDialog.user?.id, confirmDialog.action)}
            variant="contained"
            color={confirmDialog.action === 'reject' ? 'error' : 'primary'}
            disabled={actionLoading}
          >
            {confirmDialog.action === 'approve' && 'Goedkeuren'}
            {confirmDialog.action === 'reject' && 'Afwijzen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog (Companies) */}
      <Dialog
        open={confirmDeleteCompanyDialog.open}
        onClose={() => setConfirmDeleteCompanyDialog({ open: false, company: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bedrijf Verwijderen</DialogTitle>
        <DialogContent>
          <Typography>
            Weet u zeker dat u het bedrijf <strong>{confirmDeleteCompanyDialog.company?.name}</strong> wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteCompanyDialog({ open: false, company: null })}>
            Annuleren
          </Button>
          <Button
            onClick={() => handleDeleteCompany(confirmDeleteCompanyDialog.company?.id)}
            variant="contained"
            color="error"
          >
            Verwijderen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserManagementPage; 